import { ArrowRight, Zap, Tag, Users, Calendar, MapPin, ExternalLink } from 'lucide-react';

// ─────────────────────────────────────────────
// Option 1: Slim Announcement Strip
// ─────────────────────────────────────────────
const Option1Strip = () => (
  <div>
    <p className="font-mono text-xs text-[var(--text-muted)] uppercase tracking-widest mb-3">Option 1 — Slim Announcement Strip</p>
    <div className="border border-[var(--border)] p-3 mb-1 opacity-40 text-center font-mono text-xs text-[var(--text-muted)]">[ EventBanner above this ]</div>
    <div className="bg-[var(--surface)] border border-[var(--primary)] px-4 py-2 flex flex-wrap items-center justify-between gap-2">
      <div className="flex items-center gap-2">
        <span className="w-2 h-2 rounded-full bg-[var(--primary)] animate-pulse" />
        <span className="font-mono text-sm text-[var(--text-main)]">
          <span className="text-[var(--primary)]">Byt3con × Vulncon</span> — Combo Pass now live. Save up to 20% on Training + Conference.
        </span>
      </div>
      <a href="#" className="font-mono text-xs text-[var(--primary)] border border-[var(--primary)] px-3 py-1 hover:bg-[var(--primary)] hover:text-black transition-colors whitespace-nowrap flex items-center gap-1">
        Get Combo Pass <ArrowRight className="w-3 h-3" />
      </a>
    </div>
    <div className="border border-[var(--border)] p-3 mt-1 opacity-40 text-center font-mono text-xs text-[var(--text-muted)]">[ Upcoming Trainings below this ]</div>
  </div>
);

// ─────────────────────────────────────────────
// Option 2: Dedicated Combo Pass Section
// ─────────────────────────────────────────────
const Option2ComboSection = () => (
  <div>
    <p className="font-mono text-xs text-[var(--text-muted)] uppercase tracking-widest mb-3">Option 2 — Dedicated Combo Pass Section</p>
    <section className="py-12 border-t border-[var(--border)] bg-[var(--surface)]">
      <div className="max-w-4xl mx-auto px-4">
        <div className="text-center mb-8">
          <p className="font-mono text-[var(--primary)] text-xs uppercase tracking-widest mb-2">./partnership</p>
          <h2 className="text-2xl md:text-3xl font-bold text-[var(--text-main)]">
            Byt3con <span className="text-[var(--primary)]">×</span> Vulncon 2026
          </h2>
          <p className="text-[var(--text-muted)] font-mono text-sm mt-2">One ticket. Two experiences. Maximum value.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-stretch mb-6">
          {/* Byt3con card */}
          <div className="border border-[var(--primary)] p-5 bg-[var(--background)] flex flex-col gap-3">
            <p className="font-mono text-[var(--primary)] text-xs uppercase tracking-widest">Byt3con</p>
            <h3 className="font-mono font-bold text-[var(--text-main)] text-lg">Hands-on Trainings</h3>
            <ul className="space-y-1">
              {['3-Day & 1-Day workshops', 'Expert instructors', 'Jun 15–17, Bengaluru'].map(f => (
                <li key={f} className="flex items-center gap-2 font-mono text-sm text-[var(--text-muted)]">
                  <span className="text-[var(--primary)]">▸</span> {f}
                </li>
              ))}
            </ul>
            <p className="font-mono text-[var(--text-muted)] text-sm mt-auto">From <span className="text-[var(--text-main)] font-bold">₹14,999</span></p>
          </div>

          {/* Combo badge */}
          <div className="border-2 border-[var(--primary)] p-5 bg-[var(--surface-highlight)] flex flex-col items-center justify-center gap-3 relative">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[var(--primary)] text-black font-mono text-xs font-bold px-3 py-1 uppercase tracking-widest">
              Combo Pass
            </div>
            <Zap className="w-8 h-8 text-[var(--primary)]" />
            <p className="font-mono text-[var(--text-muted)] text-sm text-center">Training + Conference</p>
            <div className="text-center">
              <p className="font-mono text-3xl font-bold text-[var(--primary)]">Save 20%</p>
              <p className="font-mono text-xs text-[var(--text-muted)] mt-1">vs. buying separately</p>
            </div>
            <a href="#" className="w-full bg-[var(--primary)] text-black font-mono font-bold text-sm py-2 text-center uppercase tracking-widest hover:opacity-90 transition-opacity mt-2">
              Get Combo Pass
            </a>
          </div>

          {/* Vulncon card */}
          <div className="border border-[var(--border)] p-5 bg-[var(--background)] flex flex-col gap-3">
            <p className="font-mono text-[var(--text-muted)] text-xs uppercase tracking-widest">Vulncon</p>
            <h3 className="font-mono font-bold text-[var(--text-main)] text-lg">Security Conference</h3>
            <ul className="space-y-1">
              {['Talks & Panels', 'CTF Competitions', 'Jun 15–17, Bengaluru'].map(f => (
                <li key={f} className="flex items-center gap-2 font-mono text-sm text-[var(--text-muted)]">
                  <span className="text-[var(--text-muted)]">▸</span> {f}
                </li>
              ))}
            </ul>
            <p className="font-mono text-[var(--text-muted)] text-sm mt-auto">Conference ticket included</p>
          </div>
        </div>

        <p className="text-center font-mono text-xs text-[var(--text-muted)]">
          Already registered for one? <a href="#" className="text-[var(--primary)] hover:underline">Upgrade to combo →</a>
        </p>
      </div>
    </section>
  </div>
);

// ─────────────────────────────────────────────
// Option 3: Dual-Brand Split Hero Banner
// ─────────────────────────────────────────────
const Option3SplitHero = () => (
  <div>
    <p className="font-mono text-xs text-[var(--text-muted)] uppercase tracking-widest mb-3">Option 3 — Dual-Brand Split Hero (replaces EventBanner)</p>
    <div className="border border-[var(--border)] overflow-hidden">
      {/* Top bar */}
      <div className="bg-[var(--surface)] border-b border-[var(--border)] px-4 py-2 flex items-center gap-2">
        <span className="w-2 h-2 rounded-full bg-[var(--primary)] animate-pulse" />
        <span className="font-mono text-xs text-[var(--text-muted)] uppercase tracking-widest">Byt3con 2026 · Jun 15–17 · Royal Orchid, Bengaluru</span>
      </div>

      {/* Split body */}
      <div className="grid grid-cols-1 md:grid-cols-2">
        {/* Left — Byt3con */}
        <div className="p-6 md:p-8 border-b md:border-b-0 md:border-r border-[var(--primary)] bg-[var(--surface-highlight)] flex flex-col gap-3">
          <p className="font-mono text-[var(--primary)] text-xs uppercase tracking-widest">Byt3con Academy</p>
          <h2 className="font-mono font-bold text-[var(--text-main)] text-xl">Hands-on Security Trainings</h2>
          <p className="font-mono text-sm text-[var(--text-muted)]">3-Day & 1-Day workshops led by practitioners. Online + Physical.</p>
          <div className="flex flex-wrap gap-2 mt-1">
            {['Web AppSec', 'Cloud Security', 'Mobile Hacking'].map(t => (
              <span key={t} className="font-mono text-xs border border-[var(--primary)] text-[var(--primary)] px-2 py-1">{t}</span>
            ))}
          </div>
          <a href="#" className="font-mono text-sm text-[var(--primary)] border border-[var(--primary)] px-4 py-2 text-center hover:bg-[var(--primary)] hover:text-black transition-colors mt-2 self-start">
            View Trainings →
          </a>
        </div>

        {/* Right — Vulncon */}
        <div className="p-6 md:p-8 flex flex-col gap-3 bg-[var(--background)]">
          <p className="font-mono text-[var(--text-muted)] text-xs uppercase tracking-widest">Strategic Partner · Vulncon</p>
          <h2 className="font-mono font-bold text-[var(--text-main)] text-xl">Security Conference</h2>
          <p className="font-mono text-sm text-[var(--text-muted)]">Talks, panels, and CTF competitions running alongside trainings.</p>
          <div className="flex flex-wrap gap-2 mt-1">
            {['Talks', 'CTF', 'Networking'].map(t => (
              <span key={t} className="font-mono text-xs border border-[var(--border)] text-[var(--text-muted)] px-2 py-1">{t}</span>
            ))}
          </div>
          <a href="#" className="font-mono text-sm text-[var(--text-muted)] border border-[var(--border)] px-4 py-2 text-center hover:border-[var(--primary)] hover:text-[var(--primary)] transition-colors mt-2 self-start">
            Learn about Vulncon →
          </a>
        </div>
      </div>

      {/* Combo bar */}
      <div className="bg-[var(--primary)] px-6 py-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Tag className="w-4 h-4 text-black" />
          <span className="font-mono text-sm font-bold text-black uppercase tracking-widest">Combo Pass — Save 20% · Training + Conference bundled</span>
        </div>
        <a href="#" className="font-mono text-xs font-bold text-black border border-black px-3 py-1 hover:bg-black hover:text-[var(--primary)] transition-colors whitespace-nowrap">
          Get Combo Pass
        </a>
      </div>
    </div>
  </div>
);

// ─────────────────────────────────────────────
// Option 4: Enhanced Strategic Partners Section
// ─────────────────────────────────────────────
const Option4EnhancedPartners = () => (
  <div>
    <p className="font-mono text-xs text-[var(--text-muted)] uppercase tracking-widest mb-3">Option 4 — Enhanced Strategic Partners Section</p>
    <section className="py-12 border-t border-[var(--border)]">
      <div className="max-w-4xl mx-auto px-4">
        <div className="text-center mb-8">
          <p className="font-mono text-[var(--primary)] text-xs uppercase tracking-widest mb-2">Strategic Partners</p>
          <h2 className="text-2xl font-bold text-[var(--text-main)]">Our Strategic Partners</h2>
        </div>

        {/* Vulncon featured partner card */}
        <div className="border border-[var(--border)] hover:border-[var(--primary)] transition-colors p-6 bg-[var(--surface)] flex flex-col md:flex-row items-start md:items-center gap-6">
          {/* Logo placeholder */}
          <div className="w-28 h-16 bg-[var(--surface-highlight)] border border-[var(--border)] flex items-center justify-center flex-shrink-0">
            <span className="font-mono text-[var(--primary)] font-bold text-lg">VULNCON</span>
          </div>

          <div className="flex-1 min-w-0">
            <h3 className="font-mono font-bold text-[var(--text-main)] mb-1">Vulncon — Security Conference Partner</h3>
            <p className="font-mono text-sm text-[var(--text-muted)] mb-3">
              Vulncon is running the conference while Byt3con runs the trainings — same venue, same dates. Attend both and go deeper.
            </p>
            <div className="flex flex-wrap gap-2">
              <span className="flex items-center gap-1 font-mono text-xs text-[var(--text-muted)] border border-[var(--border)] px-2 py-1">
                <Calendar className="w-3 h-3" /> Jun 15–17, 2026
              </span>
              <span className="flex items-center gap-1 font-mono text-xs text-[var(--text-muted)] border border-[var(--border)] px-2 py-1">
                <MapPin className="w-3 h-3" /> Royal Orchid, Bengaluru
              </span>
            </div>
          </div>

          {/* Combo callout */}
          <div className="border border-[var(--primary)] p-4 bg-[var(--surface-highlight)] flex flex-col items-center gap-2 flex-shrink-0 min-w-[140px]">
            <div className="bg-[var(--primary)] text-black font-mono text-xs font-bold px-2 py-0.5 uppercase">Combo Pass</div>
            <p className="font-mono text-2xl font-bold text-[var(--primary)]">20% OFF</p>
            <p className="font-mono text-xs text-[var(--text-muted)] text-center">Training + Conference</p>
            <a href="#" className="font-mono text-xs text-black bg-[var(--primary)] px-3 py-1.5 font-bold hover:opacity-90 transition-opacity w-full text-center mt-1">
              Get Pass →
            </a>
          </div>
        </div>
      </div>
    </section>
  </div>
);

// ─────────────────────────────────────────────
// Main Preview Page
// ─────────────────────────────────────────────
const VulnconPreviewPage = () => (
  <div className="min-h-screen bg-[var(--background)] py-12">
    <div className="max-w-5xl mx-auto px-4 space-y-16">
      <div className="text-center">
        <p className="font-mono text-[var(--primary)] text-xs uppercase tracking-widest mb-2">Design Preview</p>
        <h1 className="font-mono font-bold text-[var(--text-main)] text-2xl md:text-3xl">Byt3con × Vulncon Marketing Options</h1>
        <p className="font-mono text-sm text-[var(--text-muted)] mt-2">Review all 4 concepts. Tell me which one (or mix) you want to implement.</p>
      </div>

      <div className="space-y-1">
        <Option1Strip />
      </div>

      <div className="h-px bg-[var(--border)]" />

      <div>
        <Option2ComboSection />
      </div>

      <div className="h-px bg-[var(--border)]" />

      <div>
        <Option3SplitHero />
      </div>

      <div className="h-px bg-[var(--border)]" />

      <div>
        <Option4EnhancedPartners />
      </div>

      <div className="text-center border-t border-[var(--border)] pt-8">
        <p className="font-mono text-sm text-[var(--text-muted)]">This is a temporary preview page. It will be removed after you pick an option.</p>
      </div>
    </div>
  </div>
);

export default VulnconPreviewPage;
